import { useState } from 'react'
import { logger } from './logging'

export enum StorageType {
  LOCAL = 'local',
  SESSION = 'session',
}

const getEngineForType = (storageType: StorageType) => {
  if (storageType === StorageType.LOCAL) {
    return localStorage
  }
  return sessionStorage
}

const fallbackStorageData: Map<string, any> = new Map()

const fallbackStorage: Storage = {
  getItem(key: string): string | null {
    if (key in fallbackStorageData) {
      return fallbackStorageData.get(key)
    }
    return null
  },
  removeItem(key: string) {
    fallbackStorageData.delete(key)
  },
  setItem(key: string, value: string | undefined) {
    fallbackStorageData.set(key, value)
  },
  get length() {
    return fallbackStorageData.size
  },
  clear() {
    fallbackStorageData.clear()
  },
  key(index: number) {
    return Array.from(fallbackStorageData.keys())[index]
  },
}

const isAvailable = (engine: any) => {
  return typeof engine === 'object'
}

const getStorageEngine = (storageType: StorageType): Storage => {
  const engine = getEngineForType(storageType)
  if (isAvailable(engine)) {
    return engine
  }
  return fallbackStorage
}

/**
 * Hook for accessing local or session storage with a fallback to JS memory
 * @param key the storage key
 * @param value
 * @param storageType what type of storage to use. Options are local, session, or temporary (JS memory)
 * @returns the current stored value and a function to set a new stored value.
 */
export function useStorageState<T>(
  key: string,
  value?: T | (() => T),
  storageType: StorageType = StorageType.LOCAL
): [T | undefined, (data?: T) => void] {
  const storageEngine = getStorageEngine(storageType)
  const [storageState, setStorageState] = useState<T | undefined>(() => parseValue(storageEngine.getItem(key)) ?? value)

  /**
   * Set a stored value for the key.
   * @param data The object to store
   */
  function setStorageAndState(data?: T): void {
    setStorageState(data)
    if (data === undefined || data === null) {
      storageEngine.removeItem(key)
    } else {
      storageEngine.setItem(key, JSON.stringify(data))
    }
  }

  return [storageState, setStorageAndState]
}

/**
 * Parses a string from storage into a typed object
 * @returns The stored object
 */
export function parseValue(raw?: string | null): any {
  if (!raw) return undefined
  try {
    return JSON.parse(raw)
  } catch (e: unknown) {
    logger.error('parseValue failed with error', { error: e })
    return undefined
  }
}
